import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { PaymentMethod, PlatformPayment } from '@techsofit/api-types';
import { Card, Input, Skeleton, EmptyState } from '@techsofit/ui';
import { getGyms, getPlatformPayments, getRevenue } from '../lib/adminApi';
import { useAsync } from '../lib/useAsync';
import { money } from '../lib/format';

const METHODS: { value: PaymentMethod; label: string }[] = [
  { value: 'CASH', label: 'Cash' },
  { value: 'ECOCASH', label: 'EcoCash' },
  { value: 'ONEMONEY', label: 'OneMoney' },
  { value: 'INNBUCKS', label: 'InnBucks' },
  { value: 'BANK_TRANSFER', label: 'Bank transfer' },
  { value: 'ONLINE', label: 'Online' },
  { value: 'OTHER', label: 'Other' },
];

function methodLabel(m: PaymentMethod) {
  return METHODS.find((x) => x.value === m)?.label ?? m;
}

/** Cross-gym SaaS payment ledger — every gym_subscription_payment row. */
export function RevenuePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: revenue } = useAsync(getRevenue);
  const { data: gyms } = useAsync(getGyms);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const gymId = searchParams.get('gym') ?? '';
  const { data: rows, loading, error } = useAsync(
    () => getPlatformPayments({ from: from || undefined, to: to || undefined }),
    [from, to],
  );

  const filtered = useMemo(() => {
    const list = rows ?? [];
    if (!gymId) return list;
    return list.filter((p) => p.gymId === gymId);
  }, [rows, gymId]);
  const selectedGym = gyms?.find((g) => g.id === gymId);

  const stats = [
    { label: 'Recurring / mo (MRR)', value: revenue?.mrr == null ? '—' : money(revenue.mrr) },
    { label: 'Collected this month', value: revenue?.collectedThisMonth == null ? '—' : money(revenue.collectedThisMonth) },
    { label: 'Collected all-time', value: revenue?.collectedAllTime == null ? '—' : money(revenue.collectedAllTime) },
    {
      label: 'Paying gyms',
      value: revenue?.activeGyms == null ? '—' : `${revenue.activeGyms}${revenue.pastDueGyms ? ` · ${revenue.pastDueGyms} past due` : ''}`,
    },
  ];

  return (
    <section className="mt-6 animate-fade-up">
      <div className="mb-4">
        <h1 className="font-display text-display-md text-ink">Revenue</h1>
        <p className="mt-1 text-sm text-ink-soft">
          Every subscription payment across the platform — which gym, when, and how much.
          Open a gym to see its full payment history, including member payments.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((s) => (
          <Card key={s.label} className="p-4">
            <p className="text-xs font-medium text-muted">{s.label}</p>
            <p className="mt-1 font-display text-2xl font-semibold tabular text-ink">{s.value}</p>
          </Card>
        ))}
      </div>

      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
        <Input label="From" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        <Input label="To" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        {gyms && gyms.length > 0 && (
          <label className="flex min-w-[12rem] flex-1 flex-col gap-1.5 text-sm">
            <span className="font-medium text-ink-soft">Gym</span>
            <select
              value={gymId}
              onChange={(e) => {
                const next = new URLSearchParams(searchParams);
                if (e.target.value) next.set('gym', e.target.value);
                else next.delete('gym');
                setSearchParams(next, { replace: true });
              }}
              className="h-touch rounded-control border border-line bg-elevated px-3 text-sm text-ink focus:border-ink-soft"
            >
              <option value="">All gyms</option>
              {gyms.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
          </label>
        )}
        {selectedGym && (
          <button
            type="button"
            onClick={() => navigate(`/gyms/${selectedGym.id}?tab=payments`)}
            className="h-touch shrink-0 text-sm font-medium text-ink-soft hover:text-ink"
          >
            Open {selectedGym.name} payments
          </button>
        )}
      </div>

      <Card className="mt-4 overflow-hidden p-0">
        <div className="hidden grid-cols-[1.4fr_1fr_0.8fr_0.8fr_0.8fr] gap-4 border-b border-line px-5 py-3 text-eyebrow uppercase text-muted md:grid">
          <span>Gym</span>
          <span>Plan</span>
          <span>Paid on</span>
          <span>Method</span>
          <span className="text-right">Amount</span>
        </div>
        {loading ? (
          <div className="flex flex-col gap-2 p-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-12" />
            ))}
          </div>
        ) : error ? (
          <EmptyState title="Couldn’t load payments" description={error} className="rounded-none shadow-none" />
        ) : filtered.length ? (
          <ul className="divide-y divide-line">
            {filtered.map((p: PlatformPayment) => (
              <li
                key={p.id}
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/gyms/${p.gymId}?tab=payments`)}
                onKeyDown={(e) => e.key === 'Enter' && navigate(`/gyms/${p.gymId}?tab=payments`)}
                className="grid cursor-pointer grid-cols-[1fr_auto] items-center gap-3 px-4 py-3 transition-colors hover:bg-surface md:grid-cols-[1.4fr_1fr_0.8fr_0.8fr_0.8fr] md:px-5"
              >
                <div className="min-w-0">
                  <p className="truncate font-medium text-ink">{p.gymName}</p>
                  <p className="truncate text-sm text-muted md:hidden">
                    {p.paidOn} · {p.planName}
                  </p>
                </div>
                <span className="hidden text-sm text-ink-soft md:block">{p.planName}</span>
                <span className="hidden text-sm tabular text-ink-soft md:block">{p.paidOn}</span>
                <span className="hidden text-sm text-ink-soft md:block">{methodLabel(p.method)}</span>
                <span className="justify-self-end text-sm font-medium tabular text-ink">{money(p.amount)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState
            title="No payments in this window"
            description="Record a gym subscription payment, or widen the dates."
            className="rounded-none shadow-none"
          />
        )}
      </Card>
    </section>
  );
}
